import { inject, Injectable } from '@angular/core';
import { DateRangePresetPeriod } from '@vendasta/galaxy/datepicker';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import LocalizedFormat from 'dayjs/plugin/localizedFormat';
import RelativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';
import { BehaviorSubject, catchError, combineLatest, Observable, of } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay, take } from 'rxjs/operators';
import { alignLocalDateToUTC, FormattingOptions, getDateBoundaries, getFormattingOption } from './time-range-shared';
import { filterNullAndUndefined } from '@vendasta/rx-utils';
import { LocationsService } from '../locations';
import { PartnerMarketConfigService } from '../partner-market-config';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';

dayjs.extend(LocalizedFormat);
dayjs.extend(advancedFormat);
dayjs.extend(RelativeTime);
dayjs.extend(utc);

export type TimeRangeKey = '7days' | '30days' | '90days' | '6months' | '12months' | 'custom' | 'month' | 'quarter';

export interface TimeRangeOptionBase {
  key: TimeRangeKey;
  glxyKey: DateRangePresetPeriod;
  name: string;
  i18nName?: string;
}

export interface TimeRangeOption extends TimeRangeOptionBase {
  dates?: string;
  dateRange?: [Date, Date];
}

export const AvailableTimeRangeOptions = [
  {
    key: '7days',
    glxyKey: DateRangePresetPeriod.lastSevenDays,
    name: 'Last 7 days',
    i18nName: 'COMMON.TIME_PERIOD.LAST_7_DAYS',
  },
  {
    key: '30days',
    glxyKey: DateRangePresetPeriod.lastThirtyDays,
    name: 'Last 30 days',
    i18nName: 'COMMON.TIME_PERIOD.LAST_30_DAYS',
  },
  {
    key: '90days',
    glxyKey: DateRangePresetPeriod.lastNinetyDays,
    name: 'Last 90 days',
    i18nName: 'COMMON.TIME_PERIOD.LAST_90_DAYS',
  },
  {
    key: '6months',
    glxyKey: DateRangePresetPeriod.lastSixMonths,
    name: 'Last 6 months',
    i18nName: 'COMMON.TIME_PERIOD.LAST_6_MONTHS',
  },
  {
    key: '12months',
    glxyKey: DateRangePresetPeriod.lastTwelveMonths,
    name: 'Last 12 months',
    i18nName: 'COMMON.TIME_PERIOD.LAST_12_MONTHS',
  },
  {
    key: 'custom',
    glxyKey: DateRangePresetPeriod.custom,
    name: 'Custom',
    i18nName: 'COMMON.TIME_PERIOD.SEASON',
  },
  {
    key: 'month',
    glxyKey: DateRangePresetPeriod.byMonth,
    name: 'Month',
    i18nName: 'COMMON.TIME_PERIOD.SEASON',
  },
  {
    key: 'quarter',
    glxyKey: DateRangePresetPeriod.byQuarter,
    name: 'Quarter',
    i18nName: 'COMMON.TIME_PERIOD.SEASON',
  },
] as const satisfies TimeRangeOptionBase[];

// These are the keys that are not time ranges that only take the current date into account
export const CUSTOM_TIME_RANGE_KEYS = ['custom', 'month', 'quarter'];

@Injectable({
  providedIn: 'root',
})
export class TimeRangeService {
  private readonly availableTimeRangeOptions$$ = new BehaviorSubject<TimeRangeOption[]>(
    AvailableTimeRangeOptions.map((option: TimeRangeOptionBase): TimeRangeOption => {
      if (CUSTOM_TIME_RANGE_KEYS.includes(option.key)) {
        return { ...option };
      }
      const [startDate, endDate] = getDateBoundaries(option.key);
      const start = startDate.format('MMM Do, YYYY');
      const end = endDate.format('MMM Do, YYYY');
      return {
        ...option,
        dates: `(${start} - ${end})`,
        dateRange: [startDate.toDate(), endDate.toDate()],
      };
    }),
  );

  private readonly selectedTimeRangeKey$$ = new BehaviorSubject<TimeRangeKey | null>(null);
  public readonly initialized$ = this.selectedTimeRangeKey$$.pipe(map(Boolean), distinctUntilChanged());
  public readonly selectedTimeRangeOption$: Observable<TimeRangeOption>;

  public readonly selectedTimeRangeKey$ = this.selectedTimeRangeKey$$.pipe(filterNullAndUndefined());
  public readonly dateRange$: Observable<[Date, Date]>; // these are local aligned calendar concepts (start of day/month in local)

  public utcAlignedDateRange$: Observable<[Date, Date]>; // utc aligned version of dateRange$
  public formattingOption$: Observable<FormattingOptions>; // formatting option applicable to UI
  public startDate$: Observable<Date>;
  public endDate$: Observable<Date>;

  locationsService = inject(LocationsService);
  partnerMarketConfigService = inject(PartnerMarketConfigService);
  router = inject(Router);
  activatedRoute = inject(ActivatedRoute);

  constructor() {
    this.initializeTimeRange();

    this.selectedTimeRangeOption$ = combineLatest([this.availableTimeRangeOptions$$, this.selectedTimeRangeKey$]).pipe(
      map(([options, selectedKey]) => {
        return options.find((timeRange) => timeRange.key === selectedKey);
      }),
      distinctUntilChanged((prev, current) => {
        if (prev === current) return true;
        const keysMatch = prev.key === current.key;
        const startDatesMatch = prev.dateRange[0].getTime() === current.dateRange[0].getTime();
        const endDatesMatch = prev.dateRange[1].getTime() === current.dateRange[1].getTime();
        return keysMatch && startDatesMatch && endDatesMatch;
      }),
      shareReplay({ refCount: false, bufferSize: 1 }),
    );

    this.dateRange$ = this.selectedTimeRangeOption$.pipe(
      map((dr) => dr.dateRange),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.utcAlignedDateRange$ = this.dateRange$.pipe(
      map(([startDate, endDate]: [Date, Date]): [Date, Date] => {
        return [alignLocalDateToUTC(startDate), alignLocalDateToUTC(endDate)];
      }),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.startDate$ = this.dateRange$.pipe(
      map((tr) => tr[0]),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
    this.endDate$ = this.dateRange$.pipe(
      map((tr) => tr[1]),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.formattingOption$ = this.dateRange$.pipe(
      map((dateRange) => getFormattingOption(dateRange[0], dateRange[1])),
      distinctUntilChanged(),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  public setTimeRange(timeRangeKey: TimeRangeKey): void {
    let opt = this.getTimeRangeByKey(timeRangeKey);
    // very few partner would have passed in tr="None" (string) value and pass all the checks before
    // the if is to handle "None" and other similar but unidentified cases
    if (!opt) {
      timeRangeKey = '7days';
      opt = this.getTimeRangeByKey(timeRangeKey);
    }
    if (CUSTOM_TIME_RANGE_KEYS.includes(opt.key)) {
      // Use the custom
      this.setTimeRangeKey(opt.key);
    } else {
      // Derive the range from preset and now
      const [startDayjs, endDayjs] = getDateBoundaries(opt.key);
      // Update the value on the option so that it sets on the way to custom?
      const start = dayjs.utc(startDayjs).startOf('day').toDate();
      const end = dayjs.utc(endDayjs).endOf('day').toDate();
      opt.dateRange = [start, end];
      this.setDateRange(opt.dateRange, opt.key);
    }
  }

  public setDateRange(dr: [Date, Date], trKey: TimeRangeKey): void {
    this.updateTimeRangeByKey(trKey, dr);
    this.setTimeRangeKey(trKey);
  }

  private setTimeRangeKey(tr: TimeRangeKey) {
    this.selectedTimeRangeKey$$.next(tr);
  }

  getDateFilterOptions(key: TimeRangeKey): DateRangePresetPeriod | undefined {
    return AvailableTimeRangeOptions.find((tro) => tro.key === key).glxyKey;
  }

  private getTimeRangeByKey(trKey: TimeRangeKey) {
    return this.availableTimeRangeOptions$$.getValue().find((timeRange) => timeRange.key === trKey);
  }

  private updateTimeRangeByKey(trKey: TimeRangeKey, dateRange: [Date, Date]) {
    this.availableTimeRangeOptions$$.next(
      this.availableTimeRangeOptions$$.getValue().map((opt) => {
        if (opt.key !== trKey) return opt;
        return {
          ...opt,
          dateRange,
        };
      }),
    );
  }

  private initializeTimeRange() {
    const defaultDateRange$ = combineLatest([
      this.locationsService?.isCurrentLocationABrand$,
      this.partnerMarketConfigService.businessAppConfig$,
    ]).pipe(
      map(([brand, config]) => {
        const defaultRangeKey = brand ? config.multiLocationDefaultTimeRange : config.singleLocationDefaultTimeRange;
        return AvailableTimeRangeOptions.find((type) => type.key === defaultRangeKey)?.key || null;
      }),
      catchError(() => {
        return of('12months' as const);
      }),
    );

    const firstPageNavigationEnd$ = this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      take(1),
    );

    combineLatest([defaultDateRange$, this.initialized$, firstPageNavigationEnd$])
      .pipe(take(1))
      .subscribe(([defaultDateRange, timeRangeAlreadyInitialized]) => {
        if (timeRangeAlreadyInitialized) return;
        if (useDefaultDateRangeInitializer(this.activatedRoute) && defaultDateRange) {
          this.setTimeRange(defaultDateRange);
        }
      });
  }
}

export const hasCustomDateRangeInitializerKey = 'hasCustomDateRangeInit';

function useDefaultDateRangeInitializer(activatedRoute: ActivatedRoute): boolean {
  let route = activatedRoute.root;
  do {
    const data = route.snapshot.data;
    if ('pageOptions' in data) {
      if (data.pageOptions[hasCustomDateRangeInitializerKey]) {
        return false;
      }
    }
    route = route.firstChild;
  } while (route);
  return true;
}
